import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import { AuthProvider } from './store/AuthContext';
import { LanguageProvider, useLanguage } from './store/LanguageContext';
import AppLayout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import HomePage from './pages/HomePage';
import ToolsPage from './pages/ToolsPage';
import ToolDetailPage from './pages/ToolDetailPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
// DealsPage removed
import AdminToolsPage from './pages/AdminToolsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUserManagementPage from './pages/AdminUserManagementPage';
import AdminToolManagePage from './pages/AdminToolManagePage';
import AdminCategoryPage from './pages/AdminCategoryPage';
import AdminReviewPage from './pages/AdminReviewPage';
import AdminStatsPage from './pages/AdminStatsPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import AdminSubmissionsPage from './pages/AdminSubmissionsPage';
import AdminPaymentConfigPage from './pages/AdminPaymentConfigPage';
import AdminOrderPage from './pages/AdminOrderPage';
import AdminAssetPage from './pages/AdminAssetPage';
import DisclaimerPage from './pages/DisclaimerPage';
import PrivacyPage from './pages/PrivacyPage';
import SubmitToolPage from './pages/SubmitToolPage';
import ContactPage from './pages/ContactPage';
import FeedbackPage from './pages/FeedbackPage';
import MyOrdersPage from './pages/MyOrdersPage';
import PaymentPage from './pages/PaymentPage';
import PricingPage from './pages/PricingPage';
import CheckoutPage from './pages/CheckoutPage';
import InsightsPage from './pages/InsightsPage';
import NewsPage from './pages/NewsPage';
import NewsArticlePage from './pages/NewsArticlePage';
import FundingPage from './pages/FundingPage';
import CategoryPage from './pages/CategoryPage';
import RankingsPage from './pages/RankingsPage';
import TaskPage from './pages/TaskPage';
import { SUBMIT_PAYMENT_ENABLED } from './utils/site';
import AnalyticsBootstrap from './components/AnalyticsBootstrap';
import AdsBootstrap from './components/AdsBootstrap';
import './App.css';

/** Legacy /tools?category=slug → /category/:slug */
const ToolsCategoryRedirect: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const cat = (params.get('category') || '').trim();
  if (cat) {
    params.delete('category');
    const rest = params.toString();
    return <Navigate to={`/category/${encodeURIComponent(cat)}${rest ? `?${rest}` : ''}`} replace />;
  }
  return <ToolsPage />;
};

const PaidRoute: React.FC<{ children: React.ReactElement }> = ({ children }) =>
  SUBMIT_PAYMENT_ENABLED ? children : <Navigate to="/submit" replace />;

const AppContent: React.FC = () => {
  const { language } = useLanguage();

  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 8,
          fontSize: 14,
        },
      }}
    >
      <AnalyticsBootstrap />
      <AdsBootstrap />
      <AuthProvider>
        <Router>
          <Routes>
            {/* ===== 后台管理系统（独立布局） ===== */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="users" element={<AdminUserManagementPage />} />
              <Route path="tools-manage" element={<AdminToolManagePage />} />
              <Route path="categories" element={<AdminCategoryPage />} />
              <Route path="reviews" element={<AdminReviewPage />} />
              <Route path="feedback" element={<AdminFeedbackPage />} />
              <Route path="submissions" element={<AdminSubmissionsPage />} />
              <Route path="orders" element={<AdminOrderPage />} />
              <Route path="payment-config" element={<AdminPaymentConfigPage />} />
              <Route path="tools" element={<AdminToolsPage />} />
              <Route path="stats" element={<AdminStatsPage />} />
              <Route path="assets" element={<AdminAssetPage />} />
            </Route>

            {/* ===== 前台页面（公共布局） ===== */}
            <Route path="/*" element={
              <AppLayout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/tools" element={<ToolsCategoryRedirect />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/rankings/:list" element={<RankingsPage />} />
                  <Route path="/rankings" element={<Navigate to="/rankings/popular" replace />} />
                  <Route path="/task/:slug" element={<TaskPage />} />
                  <Route path="/insights" element={<InsightsPage />} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/news/:slug" element={<NewsArticlePage />} />
                  <Route path="/funding" element={<FundingPage />} />
                  <Route path="/rongzi" element={<FundingPage />} />
                  <Route path="/tools/:slug" element={<ToolDetailPage />} />
                  {/* Deals page removed */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<Navigate to="/login" replace />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/disclaimer" element={<DisclaimerPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/submit" element={<SubmitToolPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/my-orders" element={<MyOrdersPage />} />
                  <Route path="/payment" element={<PaidRoute><PaymentPage /></PaidRoute>} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/checkout" element={<PaidRoute><CheckoutPage /></PaidRoute>} />
                </Routes>
              </AppLayout>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

const App: React.FC = () => (
  <LanguageProvider>
    <AppContent />
  </LanguageProvider>
);

export default App;
